import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import * as moment from 'moment/moment';
import { FormControl } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { debounceTime } from 'rxjs/operators';

import { LanguageService } from '../../../navigation/language.service';
import { MatInput } from '@angular/material/input';

@Component({
  selector: 'app-offboarding-requirements',
  templateUrl: './offboarding-requirements.component.html',
  styleUrls: ['./offboarding-requirements.component.scss']
})
export class OffboardingRequirementsComponent implements OnInit {
  moment = moment;
  creationFormControl = new FormControl('');
  editingFormControl = new FormControl('');
  momentLang = 'en';
  preRoles = [];
  needsToBeDeleted = [];
  actionsSubs = [];
  roles = [];

  @Input() isOrderView;
  @Input() isCreating = false;
  @Input() isCompanyView = true;
  @Input() set _roles(value) {
    if (value) {
      this.roles = this.prepareRequirementForms(value);
      if (!this.preRoles.length) {
        this.preRoles = value;
      }
    }
  }
  @Input() companies = [];
  @Output() creationChange = new EventEmitter();
  @Output() mergedOrderChange = new EventEmitter();
  @Output() dialogCloseChange = new EventEmitter();

  constructor(
    private language: LanguageService
  ) { }

  ngOnInit(): void {
    this.momentLang = this.language.getLanguage();
    this.language.onLanguageChange.subscribe(v => {
      this.momentLang = v;
    });
  }

  focus(item) {
    setTimeout(() => {
      const input = document.getElementById(item);
      if (input) {
        input.focus();
      }
    }, 10);
  }

  editRequirementForm(i) {
    this.roles[i].name = this.editingFormControl.value;
    this.roles[i].needUpdate = true;
    this.roles[i].isEditing = false;
    this.editingFormControl.reset();
    if (this.isCompanyView) {
      this.onCompanyAutoCreate(i);
    }
  }

  cancelEdit(i) {
    this.roles[i].isEditing = false;
    this.editingFormControl.reset();
  }

  duplicateRole(i) {
    this.roles.unshift({
      id: null,
      needCreate: true,
      name: this.roles[i].name,
      companyId: [this.roles[i].companyId],
      createdAt: this.roles[i].createdAt,
      updatedAt: this.roles[i].updatedAt,
      actions: this.roles[i].actions.map(v => new FormControl(v.value)),
    });
    if (this.isCompanyView) {
      this.onCompanyAutoCreate(i);
    }
  }

  deleteRole(i) {
    if (this.roles[i].id) {
      this.needsToBeDeleted.push(this.roles[i]);
      this.roles.splice(i, 1);
    } else {
      this.roles.splice(i, 1);
    }
    if (this.isCompanyView) {
      this.onCreate();
      this.needsToBeDeleted = [];
    }
  }

  toggleEditingMode(i) {
    this.editingFormControl.setValue(this.roles[i].name);
    this.roles[i].isEditing = true;
    this.focus('rename');
  }

  toggleCreationMode() {
    this.isCreating = true;
    this.focus('create');
  }

  prepareRequirementForms(value) {
    return value.map(item => {
      return {
        ...item,
        isEditing: false,
        actions: item.actions.length ? item.actions.map(v => new FormControl(v)) : []
      };
    }) as any;
  }

  cancelCreationMode() {
    this.isCreating = false;
    this.creationChange.emit(false);
    this.creationFormControl.reset();
  }

  saveNewRequirementGroup() {
    this.roles.push({
      id: null,
      needCreate: true,
      name: this.creationFormControl.value,
      companyId: this.companies,
      createdAt: moment(),
      updatedAt: moment(),
      actions: [],
      chosen: false
    });
    this.isCreating = false;
    this.creationChange.emit(false);
    this.creationFormControl.reset();
    if (this.isCompanyView) {
      this.onCreate();
    }
  }

  addRequirement(item, i) {
    item.needUpdate = true;
    const newControl = new FormControl('');
    item.actions.push(newControl);
    if (this.isCompanyView) {
      this.actionsSubs.push(newControl.valueChanges.pipe(debounceTime(100)).subscribe(val => {
        this.onCompanyAutoCreate(i);
      }));
      this.onCompanyAutoCreate(i);
    }
  }

  prepareCreationInput() {
    return this.roles.map(role => {
      const res = {
        id: role.id,
        needUpdate: !!role?.needUpdate,
        needCreate: !!role?.needCreate,
        name: role.name,
        companyId: role.companyId,
        actions: role.actions ? role.actions.map(v => ({ description: v.value })) : [],
        chosen: role.chosen,
      };

      res.needUpdate = role.actions.some(v => v.touched) ? true : !!role?.needUpdate;

      return res;
    });
  }

  onDrop(event: CdkDragDrop<any>, actions, i): void {
    moveItemInArray(actions, event.previousIndex, event.currentIndex);
    this.roles[i].needUpdate = true;
    if (this.isCompanyView) {
      this.onCompanyAutoCreate(i);
    }
  }

  mergedOrderViewChange() {
    const chosen = this.roles.find(v => v.chosen);
    this.mergedOrderChange.emit({
      data: this.prepareCreationInput(),
      needsToBeDeleted: this.needsToBeDeleted,
      chosen: chosen ? { ...chosen, actions: chosen?.actions?.map(v => v.value) } : undefined
    });
  }

  onChoose(i, e) {
    this.roles.forEach(role => role.chosen = false);
    if (e) {
      this.roles[i].chosen = true;
    }
  }

  onCreate() {
    this.dialogCloseChange.emit({ data: this.prepareCreationInput(), needsToBeDeleted: this.needsToBeDeleted });
  }

  onPanelOpen(item, i) {
    if (this.isCompanyView) {
      item.actions.map(v => {
        this.actionsSubs.push(v.valueChanges.pipe(debounceTime(100)).subscribe(val => {
          item.needUpdate = true;
          this.onCompanyAutoCreate(i);
          item.needUpdate = false;
        }));
      });
    }
  }

  onPanelClosed(item) {
    if (this.isCompanyView) {
      item.needUpdate = false;
      this.actionsSubs.forEach(v => v.unsubscribe());
    }
  }

  onCompanyAutoCreate(i) {
    this.roles.filter((v, index) => i !== index).map(v => {
      const temp = v;
      temp.needUpdate = false;
      temp.needCreate = false;
      return temp;
    });
    this.onCreate();
  }
}
